import { githubOAuthConfig, oidcConfig } from "@getdevpulse/settings/browser"
import { makeAutoObservable, runInAction } from "mobx"
import { type User, UserManager, type UserManagerSettings } from "oidc-client-ts"
import { logger } from "../lib/logger"

export class AuthStore {
  user: User | null = null
  userManager: UserManager | null = null
  isLoading = false
  error: string | null = null
  isAuthenticated = false

  constructor() {
    makeAutoObservable(this)
    this.initializeOidc()
  }

  private initializeOidc() {
    const { authority, clientId } = oidcConfig

    if (!authority || !clientId) {
      logger.info("OIDC not configured, authentication disabled", {
        component: "AuthStore",
      })
      return
    }
    const config: UserManagerSettings = {
      authority,
      client_id: clientId,
      redirect_uri: oidcConfig.redirectUri || `${window.location.origin}/callback`,
      post_logout_redirect_uri: oidcConfig.postLogoutRedirectUri || window.location.origin,
      response_type: "code",
      scope: oidcConfig.scope || "openid profile email",
      automaticSilentRenew: true,
      loadUserInfo: true,
    }

    this.userManager = new UserManager(config)

    this.userManager.getUser().then((user) => {
      runInAction(() => {
        this.user = user
        this.isAuthenticated = Boolean(user && !user.expired)
      })
    })

    this.userManager.events.addUserSignedOut(() => {
      runInAction(() => {
        this.user = null
        this.isAuthenticated = false
      })
    })
  }

  async login() {
    if (!this.userManager) {
      this.error = "Authentication not configured"
      logger.warn("Login attempted without configured authentication", {
        component: "AuthStore",
      })
      return
    }

    try {
      this.isLoading = true
      this.error = null
      logger.debug("Initiating OIDC login", { component: "AuthStore" })
      logger.track("auth_login_initiated", { method: "oidc" })
      await this.userManager.signinRedirect()
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Login failed"
        this.isLoading = false
      })
      logger.error("OIDC login failed", error, { component: "AuthStore" })
      logger.track("auth_login_failed", { method: "oidc" })
    }
  }

  async loginWithGithub() {
    const { clientId } = githubOAuthConfig
    if (!clientId) {
      this.error = "GitHub authentication not configured"
      logger.warn("GitHub login attempted without configured client ID", {
        component: "AuthStore",
      })
      return
    }

    logger.debug("Initiating GitHub OAuth login", { component: "AuthStore" })
    logger.track("auth_login_initiated", { method: "github" })

    const redirectUri =
      githubOAuthConfig.redirectUri || `${window.location.origin}/auth/github/callback`
    const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=user:email`

    window.location.href = githubAuthUrl
  }

  async handleCallback() {
    if (!this.userManager) {
      this.error = "Authentication not configured"
      logger.warn("Auth callback attempted without configured authentication", {
        component: "AuthStore",
      })
      return
    }

    try {
      this.isLoading = true
      this.error = null
      logger.debug("Processing OIDC callback", { component: "AuthStore" })
      const user = await this.userManager.signinRedirectCallback()
      runInAction(() => {
        this.user = user
        this.isAuthenticated = Boolean(user && !user.expired)
        this.isLoading = false
      })
      logger.info("User authenticated successfully", {
        component: "AuthStore",
        userId: user.profile.sub,
      })
      logger.track("auth_login_success", {
        method: "oidc",
        userId: user.profile.sub,
      })
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Authentication callback failed"
        this.isLoading = false
      })
      logger.error("Authentication callback failed", error, {
        component: "AuthStore",
      })
      logger.track("auth_login_failed", { method: "oidc" })
    }
  }

  async logout() {
    if (!this.userManager) return

    try {
      this.isLoading = true
      logger.debug("User logout initiated", { component: "AuthStore" })
      logger.track("auth_logout_initiated")
      await this.userManager.signoutRedirect()
      runInAction(() => {
        this.user = null
        this.isAuthenticated = false
        this.isLoading = false
      })
      logger.info("User logged out successfully", { component: "AuthStore" })
      logger.track("auth_logout_success")
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Logout failed"
        this.isLoading = false
      })
      logger.error("Logout failed", error, { component: "AuthStore" })
      logger.track("auth_logout_failed")
    }
  }

  async silentRenew() {
    if (!this.userManager) return

    try {
      const user = await this.userManager.signinSilent()
      runInAction(() => {
        this.user = user
        this.isAuthenticated = Boolean(user && !user.expired)
      })
    } catch (error) {
      logger.error("Silent renew failed", error, {
        component: "AuthStore",
      })
    }
  }
}
